import React from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { HoverPopover } from "@/components/popover/HoverPopover";
import { Sidebar } from "@/components/sidebar";
import { Dropdown, type DropdownItem } from "@/components/sidebar/Dropdown";
import { PanelToolbar } from "@/components/sidebar/PanelToolbar";
import { ImageUploadButton } from "@/components/tiptap/Image";
import { createBlock } from "@/components/tiptap/utils/editorActions";
import { type DocumentModel } from "@/models/DocumentModel";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";

// TODO: insert Text or Math block with command menu CMD+K
export const CreateBlocksPanel = observer(function CreateBlocksPanel({
  doc,
}: {
  doc: DocumentModel;
}) {
  const t = useTranslations("sidebar");
  const { tiptap } = doc.editor;
  const {
    view: { isRightSidebarCollapsed },
  } = useStore();

  if (!tiptap) {
    return null;
  }

  const items = [
    {
      title: t("createBlocks.image.title"),
      component: (
        <ImageUploadButton
          doc={doc}
          className={twMerge(isRightSidebarCollapsed && "min-w-fit h-full")}
          tooltipPlacement={isRightSidebarCollapsed ? "left" : undefined}
          wrapperClassName={isRightSidebarCollapsed ? "p-0.75" : undefined}
        />
      ),
      renderOnPanel: true,
    },
    {
      title: t("createBlocks.code.title"),
      renderOnPanel: true,
      hintKey: "codeBlock",
      onClick: () => createBlock(doc, "code"),
      icon: <Icon name="block-format/Code" width={24} height={24} />,
    },
    {
      title: t("createBlocks.table.title"),
      renderOnPanel: true,
      hintKey: "table",
      onClick: () => createBlock(doc, "table"),
      icon: <Icon name="block-format/Table" width={24} height={24} />,
    },
    {
      title: t("createBlocks.graph.title"),
      hintKey: "graph",
      renderOnPanel: true,
      onClick: () => createBlock(doc, "graph"),
      icon: <Icon name="block-format/Graph" width={24} height={24} />,
    },
    {
      title: t("createBlocks.separationLine.title"),
      hintKey: "separationLine",
      onClick: () => createBlock(doc, "horizontalRule"),
      icon: <Icon name="block-format/Line" width={28} height={28} />,
    },
  ] satisfies (DropdownItem & { renderOnPanel?: boolean })[];

  const panelItems = items.filter((item) => item.renderOnPanel);
  const dropdownItems = items.filter((item) => !item.renderOnPanel);

  return (
    <PanelToolbar
      label={t("toolbars.insertBlock")}
      isCollapsed={isRightSidebarCollapsed}
      data-panel="create_blocks_panel"
      className="flex-1 justify-between"
    >
      {panelItems.map((item) => {
        if ("component" in item) {
          return <React.Fragment key={item.title}>{item.component}</React.Fragment>;
        }
        return (
          <Sidebar.Button
            className={twMerge(
              "rounded-8 h-10 min-w-[50px]",
              isRightSidebarCollapsed && "min-w-fit h-full",
            )}
            wrapperClassName={isRightSidebarCollapsed ? "p-0.75" : undefined}
            key={item.title}
            icon={item.icon}
            title={item.title}
            onClick={item.onClick}
            hintKey={item.hintKey}
            tooltipPlacement={isRightSidebarCollapsed ? "left" : undefined}
          />
        );
      })}

      {dropdownItems.length > 0 &&
        (isRightSidebarCollapsed ? (
          <HoverPopover
            placement="left"
            offset={14}
            itemsClassName="w-[250px]"
            items={dropdownItems}
            toggleButtonComponent={
              <Sidebar.Button
                title={t("createBlocks.more")}
                disableTooltip
                className="rounded-8 w-full h-8.5 min-h-8.5"
                wrapperClassName="p-0"
                icon={<Icon name="More" />}
              />
            }
          />
        ) : (
          <Dropdown
            toggleButtonComponent={
              <Sidebar.Button
                as="div"
                title={t("createBlocks.more")}
                className="rounded-8 w-8 h-10"
                icon={<Icon name="ChevronDown" />}
              />
            }
            items={dropdownItems}
          />
        ))}
    </PanelToolbar>
  );
});
